import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Package, 
  ClipboardList, 
  AlertTriangle, 
  TrendingUp,
  ChefHat,
  Clock,
  ArrowRight,
  CalendarDays,
  Sparkles
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { staffService } from '../../services/staffService';
import { toast } from 'sonner';

const StaffDashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upcomingMenus, setUpcomingMenus] = useState([]);
  const [upcomingLoading, setUpcomingLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
    loadUpcomingMenus();
  }, []);

  const loadDashboard = async () => {
    try {
      const data = await staffService.getDashboard();
      setDashboard(data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadUpcomingMenus = async () => {
    try {
      const data = await staffService.getMenuCalendarUpcoming(14);
      setUpcomingMenus(data || []);
    } catch (error) {
      console.error('Error loading upcoming menus:', error);
    } finally {
      setUpcomingLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const today = new Date().toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <div className="space-y-6" data-testid="staff-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Kitchen Dashboard</h1>
          <p className="text-gray-600 mt-1">{today}</p>
        </div>
        <Link to="/staff/production">
          <Button className="bg-orange-500 hover:bg-orange-600">
            <ClipboardList className="h-4 w-4 mr-2" />
            View Production
          </Button>
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm">Today's Orders</p>
                <p className="text-3xl font-bold mt-1">{dashboard?.todays_orders_count || 0}</p>
              </div>
              <div className="p-3 bg-white/20 rounded-lg">
                <ClipboardList className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Production Batches</p>
                <p className="text-3xl font-bold mt-1">{dashboard?.production_batches_today || 0}</p>
              </div>
              <div className="p-3 bg-white/20 rounded-lg">
                <ChefHat className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`${dashboard?.low_stock_count > 0 ? 'bg-gradient-to-br from-red-500 to-red-600' : 'bg-gradient-to-br from-green-500 to-green-600'} text-white`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-sm">Low Stock Items</p>
                <p className="text-3xl font-bold mt-1">{dashboard?.low_stock_count || 0}</p>
              </div>
              <div className="p-3 bg-white/20 rounded-lg">
                <AlertTriangle className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Items to Produce</p>
                <p className="text-3xl font-bold mt-1">{dashboard?.items_to_produce?.length || 0}</p>
              </div>
              <div className="p-3 bg-white/20 rounded-lg">
                <TrendingUp className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Items to Produce Today */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-semibold">Today's Production List</CardTitle>
            <Link to="/staff/production" className="text-orange-500 hover:text-orange-600 text-sm flex items-center gap-1">
              View all <ArrowRight className="h-4 w-4" />
            </Link>
          </CardHeader>
          <CardContent>
            {dashboard?.items_to_produce?.length > 0 ? (
              <div className="space-y-3">
                {dashboard.items_to_produce.map((item) => (
                  <div 
                    key={item.product_id || item.product_name}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-orange-100 rounded-lg">
                        <ChefHat className="h-4 w-4 text-orange-600" />
                      </div>
                      <span className="font-medium text-gray-900">{item.product_name}</span>
                    </div>
                    <Badge className="bg-orange-100 text-orange-700">
                      {item.quantity} units
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <ChefHat className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No production scheduled for today</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Low Stock Alerts */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-semibold">Low Stock Alerts</CardTitle>
            <Link to="/staff/inventory" className="text-orange-500 hover:text-orange-600 text-sm flex items-center gap-1">
              View inventory <ArrowRight className="h-4 w-4" />
            </Link>
          </CardHeader>
          <CardContent>
            {dashboard?.low_stock_items?.length > 0 ? (
              <div className="space-y-3">
                {dashboard.low_stock_items.map((item) => (
                  <div 
                    key={item.id}
                    className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-red-100 rounded-lg">
                        <AlertTriangle className="h-4 w-4 text-red-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{item.name}</p>
                        <p className="text-xs text-gray-500">Min: {item.minimum} {item.unit}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-red-600">{item.current} {item.unit}</p>
                      <p className="text-xs text-gray-500">in stock</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Package className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>All inventory levels are good!</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Upcoming menu specials (from admin menu calendar) */}
      <Card data-testid="staff-dashboard-upcoming-menus">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-orange-500" />
            Upcoming Menu Specials
          </CardTitle>
          <Link to="/staff/menu-calendar" className="text-orange-500 hover:text-orange-600 text-sm flex items-center gap-1" data-testid="staff-dashboard-menu-cal-link">
            View calendar <ArrowRight className="h-4 w-4" />
          </Link>
        </CardHeader>
        <CardContent>
          {upcomingLoading ? (
            <div className="text-center py-6 text-gray-400 text-sm">Loading…</div>
          ) : upcomingMenus.length > 0 ? (
            <div className="space-y-2">
              {upcomingMenus.slice(0, 5).map((m) => {
                const [y, mo, d] = m.date.split('-').map(Number);
                const dt = new Date(y, mo - 1, d);
                const label = dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                return (
                  <Link
                    key={m.date}
                    to={`/staff/menu-calendar?date=${m.date}`}
                    className="flex items-center justify-between gap-3 p-3 bg-slate-50 rounded-lg hover:bg-orange-50 hover:border-orange-300 border border-transparent transition"
                    data-testid={`staff-dashboard-menu-row-${m.date}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2 bg-orange-100 rounded-lg flex-shrink-0">
                        <Sparkles className="h-4 w-4 text-orange-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate">{m.title || 'Scheduled menu'}</p>
                        <p className="text-xs text-gray-500">{label}</p>
                      </div>
                    </div>
                    <Badge className="bg-orange-100 text-orange-700 flex-shrink-0">
                      {m.product_count} {m.product_count === 1 ? 'special' : 'specials'}
                    </Badge>
                  </Link>
                );
              })}
              {upcomingMenus.length > 5 && (
                <p className="text-xs text-gray-500 text-center pt-1">
                  +{upcomingMenus.length - 5} more in the next 14 days
                </p>
              )}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500">
              <CalendarDays className="h-10 w-10 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No scheduled menus in the next 14 days.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Recent Inventory Activity</CardTitle>
        </CardHeader>
        <CardContent>
          {dashboard?.recent_adjustments?.length > 0 ? (
            <div className="space-y-3">
              {dashboard.recent_adjustments.map((adj) => (
                <div 
                  key={`${adj.created_at}-${adj.ingredient_name}`}
                  className="flex items-center justify-between py-3 border-b last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${adj.change > 0 ? 'bg-green-100' : 'bg-amber-100'}`}>
                      <Package className={`h-4 w-4 ${adj.change > 0 ? 'text-green-600' : 'text-amber-600'}`} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{adj.ingredient_name}</p>
                      <p className="text-xs text-gray-500 capitalize">{adj.reason}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-bold ${adj.change > 0 ? 'text-green-600' : 'text-amber-600'}`}>
                      {adj.change > 0 ? '+' : ''}{adj.change}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(adj.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Clock className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No recent inventory activity</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StaffDashboard;
